// ✅ Module Entry Point Log - This MUST appear in browser console
console.log('🔧 [social-client] Module loaded at:', new Date().toISOString());

// Export generated GraphQL types and hooks
export * from './generated/graphql';

// Export GraphQL Request Client setup
import { GraphQLClient } from 'graphql-request';
import { ACCESS_TOKEN_KEY, TOKEN_KEY, TOKEN_TYPE_KEY } from '@edusense/shared-constants';

// Resolve social server URL from env or global runtime
function resolveSocialServerUrl(): string {
  try {
    const viteEnv = (typeof import.meta !== 'undefined' && (import.meta as any).env) || {};
    const winAny = (typeof window !== 'undefined') ? (window as any) : {};
    const direct = winAny.__SOCIAL_GRAPHQL_URL || viteEnv.VITE_SOCIAL_GRAPHQL_URL || process.env?.VITE_SOCIAL_GRAPHQL_URL;
    const base = winAny.__API_BASE_URL || viteEnv.VITE_API_BASE_URL || process.env?.VITE_API_BASE_URL;
    if (direct && typeof direct === 'string') return direct;
    if (base && typeof base === 'string') return `${base.replace(/\/$/, '')}/api/social/graphql`;
  } catch {}
  return 'https://api.ugot.uk/api/social/graphql';
}
const SOCIAL_SERVER_URL = resolveSocialServerUrl();
console.log('🔧 [social-client] Resolved GraphQL endpoint URL:', SOCIAL_SERVER_URL);

// 🔧 Safe GraphQL Client initialization with error handling
let socialClient: GraphQLClient;

function resolveAuthHeaderFromStorage(): string {
  try {
    if (typeof window === 'undefined') return '';
    const ls = window.localStorage;
    const ss = window.sessionStorage;

    const tryKeys = [
      ACCESS_TOKEN_KEY,
      TOKEN_KEY,
      'accessToken',
      'edusense_token',
      'auth_token',
      // Common prefixed patterns (e.g., reward_accessToken)
      'reward_accessToken',
    ].filter(Boolean) as string[];

    let token: string | null = null;
    for (const k of tryKeys) {
      token = (ls && ls.getItem(k)) || (ss && ss.getItem(k));
      if (token) break;
    }

    // Fallback: scan any key that ends with 'accessToken' (case-insensitive)
    if (!token && ls) {
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i) || '';
        if (key === 'auth_access_token') {
          continue;
        }
        if (key.toLowerCase().endsWith('accesstoken')) {
          const v = ls.getItem(key);
          if (v) { token = v; break; }
        }
      }
    }
    if (!token && ss) {
      for (let i = 0; i < ss.length; i++) {
        const key = ss.key(i) || '';
        if (key === 'auth_access_token') {
          continue;
        }
        if (key.toLowerCase().endsWith('accesstoken')) {
          const v = ss.getItem(key);
          if (v) { token = v; break; }
        }
      }
    }

    const tokenType = (ls && (ls.getItem(TOKEN_TYPE_KEY) || ls.getItem('token_type'))) || (ss && ss.getItem('token_type')) || 'Bearer';
    return token ? `${tokenType || 'Bearer'} ${token}` : '';
  } catch {
    return '';
  }
}

try {
  console.log('🔧 [social-client] Starting GraphQLClient initialization...');
  console.log('🔧 [social-client] GraphQLClient import:', { 
    type: typeof GraphQLClient, 
    isConstructor: GraphQLClient?.prototype?.constructor === GraphQLClient 
  });
  
  // Create GraphQL Client with dynamic Authorization header per request
  socialClient = new GraphQLClient(SOCIAL_SERVER_URL, {
    requestMiddleware: async (request) => {
      try {
        const auth = resolveAuthHeaderFromStorage();
        
        console.log('🔍 [social-client] Final auth header:', {
          hasToken: !!auth,
          authHeaderLength: auth.length,
          finalAuth: auth ? auth.substring(0, 30) + '...' : 'none'
        });
        
        const finalRequest = {
          ...request,
          credentials: 'include',
          headers: {
            ...(request.headers || {}),
            ...(auth ? { Authorization: auth } : {}),
          },
        };
        
        console.log('🚀 [social-client] Outgoing GraphQL request:', {
          url: SOCIAL_SERVER_URL,
          hasAuth: !!auth,
          authHeader: auth ? auth.substring(0, 30) + '...' : 'none',
          headers: finalRequest.headers,
          body: typeof request?.body === 'string' ? JSON.parse(request.body) : request?.body
        });
        
        return finalRequest;
      } catch (e) {
        console.error('🚨 [social-client] requestMiddleware error:', e);
        return request;
      }
    },
    responseMiddleware: async (response) => {
      try {
        console.log('📥 [social-client] GraphQL response received:', {
          status: response?.status,
          statusText: response?.statusText,
          hasData: !!response?.data,
          hasErrors: !!(response?.errors?.length),
          errorCount: response?.errors?.length || 0,
          errors: response?.errors?.map(e => ({ message: e.message, extensions: e.extensions })) || [],
          data: response?.data
        });
      } catch (e) {
        console.error('🚨 [social-client] responseMiddleware error:', e);
      }
    }
  });

  console.log('🔧 [social-client] GraphQLClient created successfully:', {
    type: typeof socialClient,
    hasRequestMethod: typeof socialClient?.request === 'function',
    constructorName: socialClient?.constructor?.name,
    keys: Object.keys(socialClient || {}),
    url: (socialClient as any)?.url || 'URL not accessible'
  });
  
} catch (error) {
  console.error('🔧 [social-client] Failed to create GraphQLClient:', error);
  
  // Fallback: Create a minimal mock client that will fail gracefully
  socialClient = {
    request: () => {
      throw new Error('GraphQLClient initialization failed. Please check browser console for details.');
    }
  } as GraphQLClient;
}

// Export the safely initialized client
// Helper to update Authorization header when token changes
export function refreshSocialAuthHeader() {
  try {
    const header = resolveAuthHeaderFromStorage();
    socialClient.setHeader('Authorization', header);
  } catch (e) { /* noop */ }
}

export { socialClient };

// Explicit API to set header (used by tests or app-level auth flows)
export function setSocialAuthToken(token?: string | null, tokenType: string = 'Bearer') {
  try {
    if (token) {
      socialClient.setHeader('Authorization', `${tokenType} ${token}`);
    } else {
      // Clear header
      socialClient.setHeaders({});
    }
  } catch { /* noop */ }
}

// Re-export hooks with proper typing
// All generated hooks & types are already exported above via star export.
